import { Lead } from '@/types/meta-lead.ts';

/**
 * Filtra leads
 */
export function filterLeads(
    leads: Lead[],
    page?: string,
    tag?: number,
    in_funnel?: 'all' | 'yes' | 'no',
    in_24h_window?: 'all' | 'yes' | 'no',
    search?: string
): Lead[] {
    return leads.filter(lead => {
        // Filtro por página
        if (page && lead.page_id !== page) {
            return false;
        }

        // Filtro por tag
        if (tag && !lead.tags.some(t => t.id === tag)) {
            return false;
        }

        // Filtro por funil
        if (in_funnel === 'yes' && !lead.current_funnel_id) {
            return false;
        }
        if (in_funnel === 'no' && lead.current_funnel_id) {
            return false;
        }

        // Filtro por janela 24h
        if (in_24h_window === 'yes' && !lead.in_24h_window) {
            return false;
        }
        if (in_24h_window === 'no' && lead.in_24h_window) {
            return false;
        }

        // Busca por nome
        if (search) {
            const searchLower = search.toLowerCase();
            const fullName = `${lead.nome} ${lead.sobrenome || ''}`.toLowerCase();
            if (!fullName.includes(searchLower)) {
                return false;
            }
        }

        return true;
    });
}

/**
 * Ordena leads
 */
export function sortLeads(
    leads: Lead[],
    sortBy: 'last_interaction_at' | 'created_at' | 'nome' | 'total_clicks',
    sortOrder: 'asc' | 'desc'
): Lead[] {
    const sorted = [...leads].sort((a, b) => {
        let comparison = 0;

        if (sortBy === 'last_interaction_at') {
            const dateA = a.last_interaction_at ? new Date(a.last_interaction_at).getTime() : 0;
            const dateB = b.last_interaction_at ? new Date(b.last_interaction_at).getTime() : 0;
            comparison = dateA - dateB;
        } else if (sortBy === 'created_at') {
            comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        } else if (sortBy === 'nome') {
            const nameA = `${a.nome} ${a.sobrenome || ''}`;
            const nameB = `${b.nome} ${b.sobrenome || ''}`;
            comparison = nameA.localeCompare(nameB, 'pt-BR');
        } else if (sortBy === 'total_clicks') {
            comparison = a.total_clicks - b.total_clicks;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
}

/**
 * Formata data para exibição
 */
export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
}

/**
 * Formata data relativa (ex: "há 2 horas")
 */
export function formatRelativeDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return 'agora mesmo';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
        return `há ${diffInWeeks} ${diffInWeeks === 1 ? 'semana' : 'semanas'}`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    return `há ${diffInMonths} ${diffInMonths === 1 ? 'mês' : 'meses'}`;
}

/**
 * Formata país com bandeira emoji
 */
export function formatCountryWithFlag(country?: string): string {
    if (!country) return '🌍 Não informado';

    const countryFlags: Record<string, string> = {
        'Brasil': '🇧🇷',
        'Brasil (BR)': '🇧🇷',
        'Estados Unidos': '🇺🇸',
        'Portugal': '🇵🇹',
        'Reino Unido': '🇬🇧',
        'Argentina': '🇦🇷',
        'Espanha': '🇪🇸',
        'França': '🇫🇷',
        'Alemanha': '🇩🇪',
        'Itália': '🇮🇹',
        'México': '🇲🇽',
        'Chile': '🇨🇱',
        'Colômbia': '🇨🇴',
    };

    const flag = countryFlags[country] || '🌍';
    return `${flag} ${country}`;
}

/**
 * Retorna cor do status
 */
export function getStatusColor(is_active: boolean, in_24h_window: boolean): string {
    if (!is_active) return 'gray';
    if (in_24h_window) return 'green';
    return 'orange';
}

/**
 * Retorna label do status
 */
export function getStatusLabel(is_active: boolean, in_24h_window: boolean): string {
    if (!is_active) return 'Inativo';
    if (in_24h_window) return 'Janela Aberta';
    return 'Janela Fechada';
}